/**
 * The About section at the top of an app card on the Apps page: the app's
 * summary, domain and usage context, shown as plain text.
 *
 * These three fields used to sit inside a closed "Auto-inferred context"
 * box on the card's Overview tab, inline in the page. They are the app's
 * own details, so they now lead the card, open, and have a component of
 * their own instead of adding to a page that is already over a thousand
 * lines.
 *
 * Saved text always shows, whatever the app's variants are doing. A field
 * with no text reads "Not filled in". Only when all three are empty does
 * the section say why and offer the next step: scan the first variant, or
 * add one if there is none. The variant count picks that button and
 * nothing else.
 *
 * Edit swaps the text for the same three fields the Overview tab had, with
 * the same labels, limits and save-when-you-leave-a-field behavior, rather
 * than a dialog. That keeps one way to save them.
 *
 * The "Last scanned" line names the most recent scan only. A scan fills a
 * field only while it is empty and never overwrites an edit, so it cannot
 * say where each field came from.
 */
import { useState } from 'react'
import { Loader2, Plus, Search } from 'lucide-react'
import { Button } from '@/components/ui'
import { applicationsAPI } from '@/lib/api'
import type { Application, Variant } from '@/types'

/** Application-domain labels are stored lowercase (the scan prompt's
 * example outputs are lowercase: "trademark clearance search").
 * Display them in sentence case so the field reads naturally. */
function sentenceCase(s: string): string {
  if (!s) return s
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** Compact "5 minutes ago" / "3 hours ago" / "2 days ago" formatter for
 * the "Last scanned" line. Falls through to a date string for anything
 * older than ~30 days so users get a real timestamp rather than
 * "47 days ago". */
function relativeTime(when: Date | string | undefined): string {
  if (!when) return ''
  const ts = typeof when === 'string' ? Date.parse(when) : when.getTime()
  if (!Number.isFinite(ts)) return ''
  const deltaMs = Date.now() - ts
  if (deltaMs < 0) return 'just now'
  const sec = Math.floor(deltaMs / 1000)
  if (sec < 60) return 'just now'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`
  const day = Math.floor(hr / 24)
  if (day < 30) return `${day} day${day === 1 ? '' : 's'} ago`
  return new Date(ts).toLocaleDateString()
}

/** Pick the most recent scan across an app's variants, so the line can
 * say "from <variant name>". Null when no variant has been scanned, and
 * the line is hidden. */
function mostRecentScan(variants: Variant[]): { variant: Variant; scannedAt: Date | string } | null {
  let best: { variant: Variant; scannedAt: Date | string; ts: number } | null = null
  for (const v of variants) {
    const sa = v.scan?.scannedAt
    if (!sa) continue
    const ts = typeof sa === 'string' ? Date.parse(sa) : sa.getTime()
    if (!Number.isFinite(ts)) continue
    if (!best || ts > best.ts) best = { variant: v, scannedAt: sa, ts }
  }
  return best ? { variant: best.variant, scannedAt: best.scannedAt } : null
}

interface AppAboutSectionProps {
  app: Application
  /** The app's variants, or undefined while they load. */
  variants: Variant[] | undefined
  /** The variant being scanned right now, if any. */
  scanningVariantId: string | null
  onScan: (variantId: string) => void
  onAddVariant: () => void
  /** Called after a field saves, to load the app again. */
  onSaved: () => void
}

export function AppAboutSection({ app, variants, scanningVariantId, onScan, onAddVariant, onSaved }: AppAboutSectionProps) {
  const [editing, setEditing] = useState(false)
  const summary = (app.applicationSummary || '').trim()
  const domain = sentenceCase((app.applicationDomain || '').trim())
  const usage = (app.applicationUsageContext || '').trim()
  const anyText = Boolean(summary || domain || usage)
  const recent = mostRecentScan(variants ?? [])

  return (
    <section aria-labelledby={`app-about-heading-${app.id}`}>
      <div className="flex items-center justify-between mb-1.5">
        <h3 id={`app-about-heading-${app.id}`} className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          About this app
        </h3>
        <Button variant="ghost" size="sm" onClick={() => setEditing((e) => !e)}>
          {editing ? 'Done' : 'Edit'}
        </Button>
      </div>

      {editing ? (
        <AboutFields app={app} recentKey={recent ? String(recent.scannedAt) : 'no-scan'} onSaved={onSaved} />
      ) : anyText ? (
        <div className="space-y-1">
          <p className="text-sm">{summary || <NotFilledIn />}</p>
          <p className="text-xs">
            <span className="font-medium text-muted-foreground">Domain:</span> {domain || <NotFilledIn />}
          </p>
          <p className="text-xs">
            <span className="font-medium text-muted-foreground">Who uses it:</span> {usage || <NotFilledIn />}
          </p>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-dashed px-3 py-2.5 text-xs" data-testid="about-empty">
          <span>Not filled in yet. We fill this in when you scan a variant.</span>
          {variants === undefined ? null : variants[0] ? (
            <Button size="sm" onClick={() => onScan(variants[0]!.id)} disabled={scanningVariantId !== null}>
              {scanningVariantId === variants[0].id ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <Search className="h-3 w-3 mr-1" />
              )}
              Scan {variants[0].name}
            </Button>
          ) : (
            <Button size="sm" onClick={onAddVariant}>
              <Plus className="h-3 w-3 mr-1" />
              Add variant
            </Button>
          )}
        </div>
      )}

      {recent && (
        <p className="mt-1.5 text-[10px] text-muted-foreground">
          Last scanned: from {recent.variant.name}, {relativeTime(recent.scannedAt)}
        </p>
      )}
    </section>
  )
}

function NotFilledIn() {
  return <span className="italic text-muted-foreground">Not filled in</span>
}

/** The three editable fields, as the Overview tab had them: each saves
 * when the user leaves it with a changed value. Keyed on the most recent
 * scan so a scan that fills an empty field remounts the inputs with the
 * new values. Saving one field does not touch the scan time, so it does
 * not disturb the others. */
function AboutFields({ app, recentKey, onSaved }: { app: Application; recentKey: string; onSaved: () => void }) {
  return (
    <div key={recentKey} className="space-y-2">
      <div className="space-y-1">
        <label htmlFor={`app-summary-${app.id}`} className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Application Summary</label>
        <textarea
          id={`app-summary-${app.id}`}
          className="w-full text-xs bg-background border border-border rounded p-2 resize-none leading-relaxed focus:outline-hidden focus:border-primary/50"
          rows={2}
          maxLength={256}
          placeholder="What the app does — auto-populated by the first variant scan; edit here to override."
          defaultValue={app.applicationSummary || ''}
          onBlur={(e) => {
            const val = e.target.value.trim()
            if (val !== (app.applicationSummary || '')) {
              applicationsAPI.update(app.id, { applicationSummary: val }).then(() => onSaved())
            }
          }}
        />
      </div>

      <div className="space-y-1">
        <label htmlFor={`app-domain-${app.id}`} className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Application Domain</label>
        <input
          id={`app-domain-${app.id}`}
          type="text"
          className="w-full text-xs bg-background border border-border rounded p-2 leading-relaxed focus:outline-hidden focus:border-primary/50"
          placeholder="e.g. trademark clearance search"
          maxLength={120}
          defaultValue={sentenceCase(app.applicationDomain || '')}
          onBlur={(e) => {
            const val = e.target.value.trim()
            if (val !== sentenceCase(app.applicationDomain || '')) {
              // Canonical storage is lowercase — the sentence-cased
              // displayed value would otherwise round-trip back into
              // Mongo and drift the canonical copy.
              applicationsAPI.update(app.id, { applicationDomain: val.toLowerCase() }).then(() => onSaved())
            }
          }}
        />
      </div>

      <div className="space-y-1">
        <label htmlFor={`app-usage-${app.id}`} className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Application Usage Context</label>
        <textarea
          id={`app-usage-${app.id}`}
          className="w-full text-xs bg-background border border-border rounded p-2 resize-none leading-relaxed focus:outline-hidden focus:border-primary/50"
          rows={4}
          maxLength={500}
          placeholder="Primary users, what they value, high-risk defects, default test posture"
          defaultValue={app.applicationUsageContext || ''}
          onBlur={(e) => {
            const val = e.target.value.trim()
            if (val !== (app.applicationUsageContext || '')) {
              applicationsAPI.update(app.id, { applicationUsageContext: val }).then(() => onSaved())
            }
          }}
        />
      </div>
    </div>
  )
}
